import * as React from 'react';
import Select, {
  Props as SelectProps,
  components,
  SingleValueProps,
  OptionProps,
} from 'react-select';
import { OptionModel } from '@src/shared/src/models';
import classNames from 'classnames';

type OptionOrGroup = OptionModel | { label: string; options: OptionModel[] };

type CustomSelectProps = Omit<SelectProps<SelectProps>, 'value' | 'options'> & {
  options: OptionOrGroup[];
  selectedValue: string;
  className?: string;
  useMenuPortal?: boolean;
  renderSelectedValue?: (value: OptionModel) => React.ReactNode;
  renderOption?: (option: OptionModel) => React.ReactNode;
};

const flattenOptions = (options: OptionOrGroup[]): OptionModel[] =>
  options.flatMap((option) => ('options' in option ? option.options : [option]));

export const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  selectedValue,
  className,
  useMenuPortal = false,
  renderSelectedValue,
  renderOption,
  ...rest
}) => {
  const customComponents: CustomSelectProps['components'] = {};

  if (renderSelectedValue) {
    customComponents.SingleValue = (props: SingleValueProps<OptionModel>) => (
      <components.SingleValue {...props}>{renderSelectedValue(props.data)}</components.SingleValue>
    );
  }

  if (renderOption) {
    customComponents.Option = (props: OptionProps<OptionModel>) => {
      return <components.Option {...props}>{renderOption(props.data)}</components.Option>;
    };
  }

  if (useMenuPortal) {
    // Add MenuList component to handle scroll events and prevent modal from capturing them
    customComponents.MenuList = (props) => {
      return (
        <components.MenuList
          {...props}
          innerProps={{
            ...props.innerProps,
            onTouchMove: (e: React.TouchEvent<HTMLDivElement>) => {
              e.stopPropagation();
              props.innerProps.onTouchMove?.(e);
            },
          }}
        />
      );
    };
  }

  const getMenuPortalProps = () => {
    if (useMenuPortal) {
      return {
        menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
        menuPosition: 'fixed' as const,
        styles: {
          menuPortal: (base) => ({ ...base, zIndex: 1000 }),
        },
      };
    }
  };

  return (
    <Select
      className={classNames('react-select', className)}
      classNamePrefix="react-select"
      isClearable={false}
      isSearchable={false}
      options={options}
      components={customComponents}
      {...getMenuPortalProps()}
      {...rest}
      value={flattenOptions(options).find((option) => option.value === selectedValue)}
    />
  );
};
